import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Box, Button, Dialog, DialogContent, DialogFooter, DialogTrigger, Flex, Heading, Switch, Text, useToast } from '@chatsdet/ui';
import { API_URL } from '../../../config.js';
import {
  useNotificationDeliveryHealth,
  type NotificationDeliveryHealth,
} from '../../../features/notifications/hooks/useNotificationDeliveryHealth.js';
import {
  useUserNotificationPreferences,
  useSaveUserNotificationPreference,
  type UserNotificationEventKind,
  type UserNotificationPreference,
} from '../../../features/notifications/hooks/useUserNotificationPreferences.js';
import { SuiteCompletionAlerts } from './SuiteCompletionAlerts.js';

const USER_KIND_META: Array<{
  kind: UserNotificationEventKind;
  title: string;
  description: string;
}> = [
  {
    kind: 'workflow_test_suite_completed',
    title: 'Workflow Test Suites',
    description: 'Alerts for workflow test suites you start.',
  },
  {
    kind: 'qa_run_completed',
    title: 'QA Runs',
    description: 'Alerts for QA runs you start.',
  },
  {
    kind: 'release_qa_completed',
    title: 'Release QA',
    description: 'Alerts for release QA runs you start.',
  },
  {
    kind: 'flow_autobuild_completed',
    title: 'Flow Autobuild',
    description: 'Alerts when an autobuild you started finishes.',
  },
];

function formatPersonalPreview(pref: UserNotificationPreference, health?: NotificationDeliveryHealth) {
  const channels: string[] = [];

  if (pref.emailEnabled) {
    channels.push(health?.emailVerified ? 'Email' : 'Email unavailable');
  }

  if (pref.slackEnabled) {
    channels.push(health?.slackLinked ? 'Slack DM' : 'Slack unavailable');
  }

  if (channels.length === 0) {
    return 'No delivery channels selected.';
  }

  return channels.join(' + ');
}

export function NotificationsSection({ personalAlertsEnabled }: { personalAlertsEnabled: boolean }) {
  return (
    <Box className="space-y-8">
      <div className="space-y-2">
        <Heading level={2}>Notifications</Heading>
        <Text size="sm" className="text-muted-foreground">
          Choose where this organization's suite results are sent{personalAlertsEnabled ? ', and manage your own run alerts' : ''}.
        </Text>
      </div>

      <SuiteCompletionAlerts />

      {personalAlertsEnabled && <PersonalAlerts />}
    </Box>
  );
}

function PersonalAlerts() {
  const { toast } = useToast();
  const [editingUserPref, setEditingUserPref] = React.useState<UserNotificationPreference | null>(null);

  const prefsQuery = useUserNotificationPreferences();

  const healthQuery = useNotificationDeliveryHealth();

  const saveUserPrefMutation = useSaveUserNotificationPreference({
    onSuccess: () => toast.success('Personal alert saved'),
    onError: (error: Error) => toast.error(error.message),
  });

  const testUserPrefMutation = useMutation({
    mutationFn: async ({
      eventKind,
      channel,
    }: {
      eventKind: UserNotificationEventKind;
      channel: 'email' | 'slack';
    }) => {
      const res = await fetch(`${API_URL}/me/notifications/preferences/${eventKind}/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ channel }),
      });
      if (!res.ok) throw new Error('Failed to send personal test notification');
      return res.json();
    },
    onSuccess: () => toast.success('Personal test notification sent'),
    onError: (error: Error) => toast.error(error.message),
  });

  const prefs = prefsQuery.data?.data ?? [];
  const health = healthQuery.data?.data;

  const openPersonalDialog = React.useCallback((pref: UserNotificationPreference) => {
    setEditingUserPref({ ...pref });
  }, []);

  const closePersonalDialog = React.useCallback(() => {
    setEditingUserPref(null);
  }, []);

  const saveInlineUserEnabled = React.useCallback(
    async (pref: UserNotificationPreference, enabled: boolean) => {
      await saveUserPrefMutation.mutateAsync({
        eventKind: pref.eventKind,
        pref: { ...pref, enabled },
      });
    },
    [saveUserPrefMutation]
  );

  const activePersonalMeta = editingUserPref
    ? USER_KIND_META.find((item) => item.kind === editingUserPref.eventKind) ?? null
    : null;
  const personalEnabledCount = prefs.filter((pref) => pref.enabled).length;

  return (
    <Box className="space-y-8">
      <Box className="space-y-3">
        <div className="flex items-baseline justify-between">
          <div className="space-y-1">
            <Text
              size="xs"
              weight="semibold"
              className="uppercase tracking-wider text-muted-foreground"
            >
              Your Alerts
            </Text>
            <Text size="xs" className="text-muted-foreground italic">
              Personal alerts for runs you start, delivered by email or Slack DM when available.
            </Text>
          </div>
          <Text size="xs" className="text-muted-foreground tabular-nums">
            {personalEnabledCount} / {prefs.length}
          </Text>
        </div>

        <div className="rounded-lg border border-border bg-card px-4 py-3.5">
          <div className="space-y-1">
            <Text size="sm" className="text-muted-foreground">
              Email: {health?.primaryEmail ?? 'No email found'}
              {health?.emailVerified ? ' • verified' : ' • not verified'}
            </Text>
            <Text size="sm" className="text-muted-foreground">
              {health?.slackConnected
                ? `Slack connected${health.slackTeamName ? ` to ${health.slackTeamName}` : ''}${health.slackLinked ? ' • account linked' : ' • account not linked'}`
                : 'Slack not connected for this organization'}
            </Text>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
          {prefs.map((pref, index) => {
            const meta = USER_KIND_META.find((item) => item.kind === pref.eventKind);
            if (!meta) return null;

            return (
              <div
                key={pref.eventKind}
                className={`flex items-start justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40 ${
                  index === 0 ? '' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <Text weight="medium">{meta.title}</Text>
                  <Flex align="center" gap="sm" wrap="wrap" className="mt-1">
                    <Text size="sm" className="text-muted-foreground">
                      {formatPersonalPreview(pref, health)}
                    </Text>
                    <Button variant="ghost" size="sm" onPress={() => openPersonalDialog(pref)}>
                      Configure
                    </Button>
                  </Flex>
                </div>

                <Flex align="center" gap="sm" className="shrink-0">
                  <Switch
                    isSelected={pref.enabled}
                    onChange={(enabled) => void saveInlineUserEnabled(pref, enabled)}
                    isDisabled={saveUserPrefMutation.isPending}
                  />
                </Flex>
              </div>
            );
          })}
        </div>
      </Box>

      <DialogTrigger
        isOpen={editingUserPref !== null}
        onOpenChange={(open) => !open && closePersonalDialog()}
        size="md"
      >
        <span />
        <Dialog
          title={activePersonalMeta ? `Configure ${activePersonalMeta.title}` : 'Configure Personal Alert'}
          description={activePersonalMeta?.description}
        >
          <DialogContent className="space-y-5">
            {editingUserPref && (
              <>
                <div className="space-y-1">
                  <Text size="sm" className="text-muted-foreground">
                    Email destination: {health?.primaryEmail ?? 'No email found'}
                  </Text>
                  <Text size="sm" className="text-muted-foreground">
                    Slack status:{' '}
                    {health?.slackLinked
                      ? 'Linked and ready for Slack DMs'
                      : health?.slackConnected
                        ? 'Slack connected, but your account is not linked'
                        : 'Slack not connected for this organization'}
                  </Text>
                </div>

                <div className="space-y-4">
                  <Flex justify="between" align="center">
                    <div>
                      <Text weight="semibold">Email</Text>
                      <Text size="xs" className="text-muted-foreground italic">
                        Send to your account email when this run finishes.
                      </Text>
                    </div>
                    <Switch
                      isSelected={editingUserPref.emailEnabled}
                      onChange={(emailEnabled) =>
                        setEditingUserPref((current) =>
                          current ? { ...current, emailEnabled } : current
                        )
                      }
                    />
                  </Flex>

                  <Flex justify="between" align="center">
                    <div>
                      <Text weight="semibold">Slack DM</Text>
                      <Text size="xs" className="text-muted-foreground italic">
                        Send a direct message in Slack when available.
                      </Text>
                    </div>
                    <Switch
                      isSelected={editingUserPref.slackEnabled}
                      onChange={(slackEnabled) =>
                        setEditingUserPref((current) =>
                          current ? { ...current, slackEnabled } : current
                        )
                      }
                      isDisabled={!health?.slackConnected}
                    />
                  </Flex>
                </div>
              </>
            )}
          </DialogContent>
          <DialogFooter>
            <Flex justify="between" align="center" className="w-full gap-3">
              <Flex gap="sm">
                <Button
                  variant="outline"
                  onPress={() =>
                    editingUserPref &&
                    testUserPrefMutation.mutate({
                      eventKind: editingUserPref.eventKind,
                      channel: 'email',
                    })
                  }
                  isDisabled={!health?.emailVerified || !editingUserPref || testUserPrefMutation.isPending}
                >
                  Test Email
                </Button>
                <Button
                  variant="outline"
                  onPress={() =>
                    editingUserPref &&
                    testUserPrefMutation.mutate({
                      eventKind: editingUserPref.eventKind,
                      channel: 'slack',
                    })
                  }
                  isDisabled={!health?.slackLinked || !editingUserPref || testUserPrefMutation.isPending}
                >
                  Test Slack
                </Button>
              </Flex>

              <Flex gap="sm">
                <Button variant="outline" onPress={closePersonalDialog}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onPress={async () => {
                    if (!editingUserPref) return;
                    await saveUserPrefMutation.mutateAsync({
                      eventKind: editingUserPref.eventKind,
                      pref: editingUserPref,
                    });
                    closePersonalDialog();
                  }}
                  isDisabled={!editingUserPref || saveUserPrefMutation.isPending}
                >
                  Save Alert
                </Button>
              </Flex>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
